import { useState, useCallback, useMemo } from 'react';
import { IPersonaProps } from '@fluentui/react';
import { useMutation } from '@tanstack/react-query';
import { ApiCalls } from '../../../../../api/api';
import { TimeSearchRow } from '../../../../../api/generated/data-contracts';
import { mapTaskToPersona } from '../utils/mapTaskToPersona';
import { UseTaskSearchReturn } from '../types';

export const useTaskSearch = (): UseTaskSearchReturn => {
    const [selectedTask, setSelectedTask] = useState<TimeSearchRow | null>(null);

    // Task search mutation - using admin-only endpoint
    const { mutateAsync: searchTasks, data: taskSearchResults } = useMutation({
        mutationFn: (query: string) => ApiCalls.searchAllTasksAdminOnly({ query }),
        onError: error => {
            console.error('Task search error:', error);
        },
    });

    // Task picker functionality
    const onTaskResolveSuggestions = useCallback(
        (searchText: string) => {
            if (searchText) {
                return searchTasks(searchText)
                    .then(result => result.data.map(mapTaskToPersona))
                    .catch(error => {
                        console.error('Task search failed:', error);
                        return [];
                    });
            }
            return null;
        },
        [searchTasks],
    );

    const onTaskSelected = useCallback(
        (persona: IPersonaProps) => {
            if (persona && taskSearchResults) {
                const task = taskSearchResults.data.find(t => t.taskId === persona.itemID);
                if (task) {
                    setSelectedTask(task);
                    return null;
                }
            }
            return persona;
        },
        [taskSearchResults],
    );

    const onRemoveTask = useCallback(() => {
        setSelectedTask(null);
    }, []);

    // Create persona for selected task
    const selectedTaskPersona = useMemo(() => {
        if (selectedTask) {
            return mapTaskToPersona(selectedTask);
        }
    }, [selectedTask]);

    return {
        selectedTask,
        setSelectedTask,
        onTaskResolveSuggestions,
        onTaskSelected,
        onRemoveTask,
        selectedTaskPersona,
        taskSearchResults,
    };
};
